import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@/contexts/ThemeContext";

/**
 * useIsDarkMode
 * Returns a boolean indicating whether dark mode should be active.
 * Relies on ThemeProvider's theme (dark/light/system) and system preference.
 */
export function useIsDarkMode(): boolean {
  const { theme } = useTheme();

  const [prefersDark, setPrefersDark] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);

    // Keep state in sync on mount too
    setPrefersDark(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", onChange);
      return () => mediaQuery.removeEventListener("change", onChange);
    }

    // Safari fallback
    mediaQuery.addListener(onChange);
    return () => mediaQuery.removeListener(onChange);
  }, []);

  const isDark = useMemo(() => {
    if (theme === "dark") return true;
    if (theme === "light") return false;
    // system
    return prefersDark;
  }, [prefersDark, theme]);

  return isDark;
}
